// A to-do's checklist in the open card: tick, edit in place, Return for the next item, drag an item by its grip or move
// it with ⌥⌘↑↓ (⌥↑↓ too), × to delete. The order follows the iPhone's and the Mac's (core/checklist.ts, the kit's rules,
// cases/checklist.json): with "Done Items at the End" (Settings › Lists, in this browser, as the apps keep it per
// device) a ticked item moves below the open ones once the tick has been seen, and a dragged or moved item stays among
// the items of its kind. The card keeps the checklist until it closes, so a move has no step of Undo of its own (the
// Mac's card has one); ⌘Z inside an item is the browser's, for its text.

import { useEffect, useLayoutEffect, useRef, useState, type DragEvent } from "react";
import { moveItemToShown, places, shownOrder, stepPlace, submitItem } from "../core/checklist";
import { newID } from "../core/ids";
import type { ChecklistItem } from "../core/records";
import { t } from "../i18n";
import { Icon } from "./Icon";
import { usePrefs } from "./prefs";

const MIME = "application/x-todolix-checklist-item";
/** How long a ticked item keeps its place before it moves, as on the iPhone and the Mac. */
const HOLD = 600;
const reducesMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

export function Checklist({ items, onChange, focusNew }: { items: readonly ChecklistItem[]; onChange: (items: ChecklistItem[]) => void; focusNew?: boolean }) {
  const { checklistDoneLast: doneLast } = usePrefs();
  const [focus, setFocus] = useState<string | "new" | null>(focusNew ? "new" : null);
  const [adding, setAdding] = useState("");
  /** An item just ticked: it keeps its place for a moment before it moves below the open ones. */
  const [held, setHeld] = useState<string | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** The item in the hand, and where it would land: a place of the order shown with it taken out. */
  const [dragged, setDragged] = useState<string | null>(null);
  const [slot, setSlot] = useState<number | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const rows = useRef(new Map<string, HTMLElement>());
  const tops = useRef(new Map<string, number>());
  /** How long the next change of order glides, in ms; null: at once (a drop lands where its line was). */
  const motion = useRef<number | null>(200);

  const shown = shownOrder(items, doneLast, held);
  const order = shown.map((item) => item.id).join(" ");

  useEffect(() => {
    if (focus === null) return;
    const selector = focus === "new" ? "input.check-new" : `input[data-item="${focus}"]`;
    root.current?.querySelector<HTMLInputElement>(selector)?.focus();
    setFocus(null);
  }, [focus]);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  // The items glide to their new places, as a ticked item does on the iPhone and the Mac.
  useLayoutEffect(() => {
    const now = new Map<string, number>();
    for (const [id, element] of rows.current) now.set(id, element.offsetTop);
    const before = tops.current;
    tops.current = now;
    const duration = motion.current;
    motion.current = 200;
    if (duration === null || reducesMotion()) return;
    for (const [id, top] of now) {
      const was = before.get(id);
      const element = rows.current.get(id);
      if (was === undefined || was === top || typeof element?.animate !== "function") continue;
      element.animate([{ transform: `translateY(${was - top}px)` }, { transform: "none" }], { duration, easing: "cubic-bezier(.2, .8, .2, 1)" });
    }
  }, [order]);

  const update = (id: string, change: Partial<ChecklistItem>) => onChange(items.map((item) => (item.id === id ? { ...item, ...change } : item)));
  const remove = (id: string) => onChange(items.filter((item) => item.id !== id));

  /** Ticks an item or opens it again. With done items at the end, it moves there once the tick has been seen. */
  const tick = (item: ChecklistItem) => {
    update(item.id, { isDone: !item.isDone });
    if (!doneLast) return;
    clearTimeout(holdTimer.current);
    setHeld(item.id);
    holdTimer.current = setTimeout(() => {
      motion.current = 350;
      setHeld((current) => (current === item.id ? null : current));
    }, HOLD);
  };

  /** One place up or down in the order shown, among the items of its kind. */
  const step = (id: string, by: number) => {
    const target = stepPlace(items, id, by, doneLast, held);
    if (target === null) return;
    setHeld(null);
    onChange(moveItemToShown(items, id, target, doneLast));
    setFocus(id);
  };

  // MARK: Dragging, by the grip only: a click or a drag in the text stays the text field's.

  const lift = (event: DragEvent, item: ChecklistItem) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(MIME, item.id);
    const row = rows.current.get(item.id);
    if (row) event.dataTransfer.setDragImage(row, 24, row.offsetHeight / 2);
    setDragged(item.id);
    // The line stands where it was until the pointer moves.
    setSlot(shown.findIndex((other) => other.id === item.id));
  };

  /** The place under the pointer, among the items of the dragged one's kind. */
  const placeAt = (y: number, id: string): number => {
    let place = 0;
    for (const other of shown) {
      if (other.id === id) continue;
      const box = rows.current.get(other.id)?.getBoundingClientRect();
      if (box && box.top + box.height / 2 < y) place += 1;
    }
    const [first, last] = places(items, id, doneLast, held);
    return Math.min(Math.max(place, first), last);
  };

  const over = (event: DragEvent) => {
    if (dragged === null) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    const place = placeAt(event.clientY, dragged);
    if (place !== slot) setSlot(place);
  };

  const drop = (event: DragEvent) => {
    if (dragged === null) return;
    event.preventDefault();
    event.stopPropagation();
    const place = placeAt(event.clientY, dragged);
    motion.current = null;
    setHeld(null);
    onChange(moveItemToShown(items, dragged, place, doneLast));
    setDragged(null);
    setSlot(null);
  };

  const others = shown.filter((item) => item.id !== dragged);
  /** The line where the dragged item would land: above the item at the slot, or under the last. */
  const line = (item: ChecklistItem): string => {
    if (dragged === null || slot === null || item.id === dragged) return "";
    const index = others.indexOf(item);
    if (index === slot) return " drop-before";
    if (slot === others.length && index === others.length - 1) return " drop-after";
    return "";
  };

  return (
    <div className="checklist" ref={root} role="list" aria-label={t("Checklist")} onDragOver={over} onDrop={drop}>
      {shown.map((item) => (
        <div key={item.id} role="listitem" data-check={item.id}
          className={`check-item${item.isDone ? " ticked" : ""}${item.id === dragged ? " lifted" : ""}${line(item)}`}
          ref={(element) => { if (element) rows.current.set(item.id, element); else rows.current.delete(item.id); }}>
          <button className="dot" aria-label={item.isDone ? t("Mark as Not Done") : t("Mark as Done")} aria-pressed={item.isDone}
            onClick={() => tick(item)} />
          <input data-item={item.id} value={item.title} aria-label={t("Checklist Item")}
            onChange={(event) => update(item.id, { title: event.target.value })}
            onKeyDown={(event) => {
              // Return: the next item, or an empty one taken away (Todo.submitChecklistItem). ⌘Return closes the
              // card, as from the Mac's checklist.
              if (event.key === "Enter" && !event.nativeEvent.isComposing && !event.metaKey && !event.ctrlKey) {
                event.preventDefault();
                event.stopPropagation();
                const result = submitItem(items, item.id);
                onChange(result.items);
                setFocus(result.added ?? "new");
              }
              if (event.key === "Backspace" && item.title === "") {
                event.preventDefault();
                const index = shown.indexOf(item);
                remove(item.id);
                setFocus(index > 0 ? shown[index - 1]!.id : "new");
              }
              // The Mac's ⌥⌘↑↓, and ⌥↑↓, the web's keys before.
              if (event.altKey && !event.shiftKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
                event.preventDefault();
                step(item.id, event.key === "ArrowUp" ? -1 : 1);
              }
            }} />
          <button className="remove" aria-label={t("Delete {name}", { name: item.title || t("Item") })} onClick={() => remove(item.id)}><Icon name="x" /></button>
          <span className="check-grip" draggable aria-hidden="true"
            onDragStart={(event) => lift(event, item)}
            onDragEnd={() => { setDragged(null); setSlot(null); }}>
            <Icon name="grip" />
          </span>
        </div>
      ))}
      <div className="check-item adding">
        <span className="dot" aria-hidden="true" />
        <input className="check-new" value={adding} placeholder={t("Add Item")} aria-label={t("Add Item")}
          onChange={(event) => setAdding(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing) {
              event.preventDefault();
              event.stopPropagation();
              if (adding.trim() === "") return;
              onChange([...items, { id: newID(), title: adding.trim(), isDone: false }]);
              setAdding("");
            }
          }} />
      </div>
    </div>
  );
}
